import { useCallback } from "react";

import { useModalManager } from "@shapr3d/frontend-lib";
import { useParams } from "react-router-dom";

import { webAppURL } from "../config";
import { ModalIds } from "../constants/modalIds";
import { useDeepLinkStateStore } from "../stores/deepLinkStateStore";
import { ProjectRoleUtils } from "../types";
import { handleFileDownload } from "../utils/handleFileDownload";
import { usePaywall } from "./usePaywall";
import { useProject } from "./useProject";
import { useTeamActiveSubscription } from "./useTeamActiveSubscription";
import { useTracker } from "./useTracker";
import { useViewLinkId, useWebExport } from "./useWebExport";

export const useProjectActions = () => {
  const { projectId: projectIdFromParams } = useParams();
  const { data: projectData } = useProject();
  const { data: webExportData } = useWebExport();
  const viewLinkId = useViewLinkId();
  const { data: hasTeamSubscription } = useTeamActiveSubscription(projectData?.teamId);
  const tracker = useTracker();
  const { open: openModal } = useModalManager();
  const { setIsModalOpen: setIsDeepLinkModalOpen } = useDeepLinkStateStore();

  const downloadPaywall = usePaywall("download_shapr_file");
  const projectId =
    projectData?.project.projectId ?? webExportData?.webExport.projectId ?? projectIdFromParams;
  const canCopyProjectLink = Boolean(projectId);
  const canCopyViewLink = Boolean(projectId && viewLinkId);

  const handleEditInAppClick = useCallback(() => {
    const projectId = projectData?.project.projectId ?? webExportData?.webExport.projectId;

    if (projectId) {
      tracker.trackDeeplinkButtonClicked({ projectId });
    }

    setIsDeepLinkModalOpen(true);
  }, [projectData, setIsDeepLinkModalOpen, tracker, webExportData]);

  const handleCopyProjectLink = useCallback(async () => {
    if (!projectId) {
      return false;
    }

    try {
      await navigator.clipboard.writeText(`${webAppURL}/p/${projectId}`);
      return true;
    } catch {
      return false;
    }
  }, [projectId]);

  const handleCopyViewLink = useCallback(async () => {
    if (!projectId || !viewLinkId) {
      return false;
    }

    try {
      await navigator.clipboard.writeText(`${webAppURL}/p/${projectId}/v/${viewLinkId}`);
      return true;
    } catch {
      return false;
    }
  }, [projectId, viewLinkId]);

  const handleShareClick = useCallback(() => {
    openModal({ id: ModalIds.SHARE_PROJECT });
  }, [openModal]);

  const handleDownloadClick = useCallback(() => {
    if (!projectData) {
      return;
    }

    tracker.trackDownloadFileClicked({
      projectTeamId: projectData.teamId,
      projectId: projectData.project.projectId,
    });

    if (hasTeamSubscription) {
      handleFileDownload(projectData.latestRevision.fileDownload?.url);
      return;
    }

    const isOwner = ProjectRoleUtils.isAtLeast(projectData.userRole, "owner");
    downloadPaywall.open({
      isOwner,
      remoteProjectId: projectData.project.projectId,
      returnToURL: window.location.href,
    });
  }, [downloadPaywall, hasTeamSubscription, projectData, tracker]);

  return {
    canCopyProjectLink,
    canCopyViewLink,
    handleEditInAppClick,
    handleCopyProjectLink,
    handleCopyViewLink,
    handleShareClick,
    handleDownloadClick,
  };
};
